/**
 * McpIframeWidget — MCP widget HTML을 iframe으로 임베드하는 범용 위젯
 *
 * 3단계 로딩 UX (ChatGPT MCP Apps 스타일):
 *   1. HTML fetch 중 → skeleton A (fetch 표시)
 *   2. iframe 마운트 + React 부트 중 → skeleton B (widget 로드 표시)
 *   3. data 수신 + 렌더 완료 → 실제 widget (iframe 내부에서 표시)
 *
 * 동작 원리:
 * - 첫 렌더 시 `/mcp/widget` 엔드포인트에서 JSON {html} 형태로 widget HTML fetch
 *   (Supabase Edge Functions가 text/html 응답에 CSP를 자동 주입하는 문제 우회)
 * - fetch한 HTML을 iframe `srcdoc`에 주입 (parent origin에서 실행, CSP 제약 없음)
 * - iframe load 후 postMessage로 tool result (structuredContent) 전달
 * - MCP widget-shell.tsx가 _widget 필드를 보고 적절한 페이지 렌더
 * - iframe 내부에서 `datalab-widget:resize` postMessage로 높이 자동 통지
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";

/* ══════════════════════════════════════════════════════════════════════════
   Constants
   ══════════════════════════════════════════════════════════════════════════ */

function getMcpWidgetUrl(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!supabaseUrl) return "";
  return `${supabaseUrl.replace(/\/$/, "")}/functions/v1/mcp/widget`;
}

const DEFAULT_HEIGHT = 220;
const MAX_HEIGHT = 720;
const MIN_HEIGHT = 140;

/* ══════════════════════════════════════════════════════════════════════════
   Schema
   ══════════════════════════════════════════════════════════════════════════ */

const McpIframeSchema = z.object({
  _widget: z.string(),
  _toolName: z.string().optional(),
  structuredContent: z.record(z.string(), z.unknown()),
});

type McpIframeData = z.infer<typeof McpIframeSchema>;

/* ══════════════════════════════════════════════════════════════════════════
   Widget HTML Cache (module-level, 한 번 fetch 후 재사용)
   ══════════════════════════════════════════════════════════════════════════ */

let cachedHtmlPromise: Promise<string | null> | null = null;

function fetchWidgetHtml(): Promise<string | null> {
  if (cachedHtmlPromise) return cachedHtmlPromise;
  const url = getMcpWidgetUrl();
  if (!url) return Promise.resolve(null);

  cachedHtmlPromise = fetch(url, { cache: "default" })
    .then(async (resp) => {
      if (!resp.ok) return null;
      try {
        const json = (await resp.json()) as { html?: string };
        return json.html ?? null;
      } catch {
        return null;
      }
    })
    .catch(() => null);

  return cachedHtmlPromise;
}

/* ══════════════════════════════════════════════════════════════════════════
   Loading Skeleton (ChatGPT 스타일)
   ══════════════════════════════════════════════════════════════════════════ */

function LoadingSkeleton({
  message,
  className,
}: {
  message: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-center gap-2 rounded-xl border border-stroke-subtle bg-surface-raised/60",
        "animate-pulse backdrop-blur-sm",
        className,
      )}
      style={{ height: DEFAULT_HEIGHT }}
      role="status"
      aria-label={message}
    >
      <Loader2 className="size-4 animate-spin text-text-tertiary" />
      <span className="text-[12px] text-text-tertiary">{message}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════════════════════ */

export function McpIframeWidget({
  widget,
  className,
}: AgentWidgetProps<McpIframeData>) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [dataDelivered, setDataDelivered] = useState(false);
  const [height, setHeight] = useState<number>(DEFAULT_HEIGHT);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const widgetUrl = useMemo(() => getMcpWidgetUrl(), []);

  const payload = widget.data;

  /* ── Step 1: HTML fetch (모듈 캐시 재사용) ── */
  useEffect(() => {
    let cancelled = false;
    fetchWidgetHtml()
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setFetchError("위젯 HTML 로드 실패");
        } else {
          setHtml(result);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(
          err instanceof Error ? err.message : "Widget fetch error",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Step 2: iframe load 후 data 전달 (MCP Apps postMessage JSON-RPC) ── */
  useEffect(() => {
    if (!iframeReady || !html) return;
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    try {
      iframe.contentWindow.postMessage(
        {
          jsonrpc: "2.0",
          method: "ui/notifications/tool-result",
          params: {
            structuredContent: payload.structuredContent,
          },
        },
        "*",
      );
      // data 전달 완료 표시 (첫 resize notification 오면 실제 렌더 완료로 간주)
      setDataDelivered(true);
    } catch (err) {
      console.warn("[McpIframeWidget] postMessage failed:", err);
    }
  }, [iframeReady, html, payload.structuredContent]);

  /* ── Step 3: iframe 높이 자동 조정 ── */
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      const msg = event.data;
      if (
        msg &&
        typeof msg === "object" &&
        msg.type === "datalab-widget:resize" &&
        typeof msg.height === "number"
      ) {
        const clamped = Math.max(
          MIN_HEIGHT,
          Math.min(MAX_HEIGHT, Math.round(msg.height)),
        );
        setHeight((prev) => {
          // 8px deadband — 미세 변화는 무시하여 re-render 최소화
          if (Math.abs(prev - clamped) < 8) return prev;
          return clamped;
        });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  /* ── URL 미설정 or fetch 실패 시 에러 UI ── */
  if (!widgetUrl || fetchError) {
    return (
      <div
        className={cn(
          "rounded-lg border border-red-200/60 bg-red-50/60 px-4 py-3 text-[12px] text-red-700 dark:border-red-800/40 dark:bg-red-950/20 dark:text-red-300",
          className,
        )}
      >
        {fetchError ??
          "VITE_SUPABASE_URL이 설정되지 않아 MCP widget을 로드할 수 없습니다."}
      </div>
    );
  }

  /* ── Step 1 Skeleton: HTML 로딩 중 ── */
  if (!html) {
    return <LoadingSkeleton message="위젯 준비 중..." className={className} />;
  }

  return (
    <div className={cn("relative w-full", className)}>
      {/* Step 2 Skeleton (iframe 위에 overlay, data 전달 전까지만) */}
      {!dataDelivered && (
        <LoadingSkeleton
          message="데이터 로드 중..."
          className="absolute inset-0 z-10"
        />
      )}

      <iframe
        ref={iframeRef}
        srcDoc={html}
        title={widget.title}
        onLoad={() => setIframeReady(true)}
        className={cn(
          "block w-full rounded-xl border border-stroke-subtle bg-surface-raised",
          "transition-opacity duration-200",
          dataDelivered ? "opacity-100" : "opacity-0",
        )}
        style={{ height }}
        /* sandbox:
           - allow-scripts: iframe 내부 JS 실행 (필수)
           - allow-popups: 링크 클릭 시 새 창 오픈
           - allow-same-origin 제외: srcDoc + cross-origin 데이터 (보안) */
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        loading="lazy"
      />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Skeleton (registry용)
   ══════════════════════════════════════════════════════════════════════════ */

export function McpIframeWidgetSkeleton({ className }: { className?: string }) {
  return <LoadingSkeleton message="위젯 준비 중..." className={className} />;
}

/* ══════════════════════════════════════════════════════════════════════════
   Widget Definition
   ══════════════════════════════════════════════════════════════════════════ */

const mcpIframeWidget = defineWidget<McpIframeData>({
  type: "mcp-iframe",
  label: "MCP Widget (iframe)",
  schema: McpIframeSchema,
  component: McpIframeWidget,
  skeleton: McpIframeWidgetSkeleton,
  defaultLayout: "full",
  maxHeight: MAX_HEIGHT,
  aiHint:
    "MCP 서버의 React 위젯을 iframe으로 임베드 (ChatGPT/Claude Desktop과 동일)",
});

registerWidget(mcpIframeWidget);
